import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, Brush, Palette, Pencil } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { PORTRAIT_MEDIUMS } from "../data/site";

const ICONS = { Brush, Palette, Pencil };

const MediumCard = ({ medium }) => {
  const Icon = ICONS[medium.icon] || Brush;
  const slug = medium.name.toLowerCase().replace(/\s+/g, "-");

  return (
    <div
      data-testid={`portrait-medium-${slug}`}
      className="brutal-border brutal-shadow brutal-press bg-white p-7 sm:p-8 flex flex-col"
    >
      <div className="w-14 h-14 rounded-2xl bg-[#EAF1F8] text-[#3E6FA8] flex items-center justify-center">
        <Icon size={26} strokeWidth={2} />
      </div>
      <h2 className="font-display text-3xl text-[#1F2530] mt-5">{medium.name}</h2>
      {medium.paper && (
        <p className="text-xs font-bold uppercase tracking-wider text-[#3E6FA8] mt-1">
          {medium.paper}
        </p>
      )}
      <p className="mt-3 leading-relaxed text-[#1F2530]/70">{medium.desc}</p>

      <p className="mt-6 text-xs font-bold uppercase tracking-[0.2em] text-[#1F2530]/60">
        {medium.sizeLabel}
      </p>
      <ul className="mt-3 space-y-2 flex-1">
        {medium.sizes.map((s) => (
          <li
            key={s.size}
            data-testid={`portrait-size-${slug}-${s.size}`}
            className="flex items-center justify-between border-b border-[#E3E5E9] pb-2 last:border-0"
          >
            <span className="font-medium text-[#1F2530]">{s.size}</span>
            <span className="text-xs font-bold uppercase tracking-wider bg-[#EAF1F8] text-[#3E6FA8] px-2.5 py-1 rounded-full">
              ${s.price}
            </span>
          </li>
        ))}
      </ul>

      {medium.note && (
        <p
          data-testid={`portrait-note-${slug}`}
          className="mt-4 text-sm font-semibold italic text-red-600"
        >
          {medium.note}
        </p>
      )}

      <Link
        to="/contact"
        state={{ service: "Commission Portraits" }}
        data-testid={`portrait-order-${slug}`}
        className="mt-6 inline-flex items-center gap-2 self-start brutal-border brutal-shadow-sm brutal-press bg-[#3E6FA8] text-white font-extrabold uppercase tracking-wider text-sm px-5 py-3"
      >
        Order {medium.name} <ArrowRight size={18} strokeWidth={2.5} />
      </Link>
    </div>
  );
};

export default function CommissionPortraits() {
  return (
    <div className="App">
      <Navbar />

      {/* HEADER */}
      <section className="bg-[#1F2530] text-[#F3F4F6] py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-6 sm:px-10">
          <Link
            to="/"
            data-testid="portraits-back"
            className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.15em] text-[#F3F4F6]/70 hover:text-[#7BA7D9] transition-colors"
          >
            <ArrowLeft size={16} strokeWidth={2.5} /> Back home
          </Link>
          <span className="block mt-8 text-xs font-bold uppercase tracking-[0.2em] text-[#7BA7D9]">
            Commission Portraits
          </span>
          <h1 className="font-display text-5xl sm:text-7xl leading-[0.95] mt-3">
            Commission a Portrait
          </h1>
          <p className="mt-5 max-w-2xl text-lg font-medium text-[#F3F4F6]/70">
            Hand-crafted portraits made from your favorite photos. Choose your medium and
            size — every piece is created with care and originality.
          </p>
        </div>
      </section>

      {/* MEDIUMS */}
      <section className="bg-[#F3F4F6] py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-6 sm:px-10">
          <div
            className="grid grid-cols-1 lg:grid-cols-3 gap-8"
            data-testid="portrait-mediums-grid"
          >
            {PORTRAIT_MEDIUMS.map((m) => (
              <MediumCard key={m.name} medium={m} />
            ))}
          </div>

          {/* CTA */}
          <div className="mt-14 brutal-border brutal-shadow bg-white p-8 sm:p-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
            <div>
              <h2 className="font-display text-3xl sm:text-4xl text-[#1F2530]">
                Ready to commission your portrait?
              </h2>
              <p className="mt-2 font-medium text-[#1F2530]/70">
                Send us your photo and details, and we'll bring it to life.
              </p>
            </div>
            <Link
              to="/contact"
              state={{ service: "Commission Portraits" }}
              data-testid="portraits-cta"
              className="shrink-0 inline-flex items-center gap-3 brutal-border brutal-shadow-sm brutal-press bg-[#3E6FA8] text-white font-extrabold uppercase tracking-wider px-7 py-4"
            >
              Start Your Order <ArrowRight size={20} strokeWidth={2.5} />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
